import { classes } from "./classes.js";
import { BANK_BG_BRAND, BANK_BG_INK, BANK_BG_NEUTRAL, BANK_RADIUS_PILL } from "./tokens.js";

/**
 * `F-0c`: a bar that says how far (prototype `.prog` and `.split`): the capacity bar, the 90/10 split of a payment.
 *
 * It is a `progressbar` with a name and a value, so the figure is not only a length. `value` is clamped to 0..100;
 * `parts` draws several fills side by side for a split and is exclusive with `value`. The fill grows in
 * (`hk-grow`) unless the person asked for less motion.
 */
export type ProgressTone = "ok" | "brand" | "ink";

export interface ProgressPart {
  readonly value: number;
  readonly tone?: ProgressTone;
}

export interface ProgressBarProps {
  /** The bar's accessible name, from the catalogue. */
  readonly label: string;
  readonly value?: number;
  readonly parts?: readonly ProgressPart[];
  readonly tone?: ProgressTone;
  readonly className?: string;
}

const clamp = (n: number): number => Math.min(100, Math.max(0, n));

const FILLS: ReadonlyMap<ProgressTone, string> = new Map<ProgressTone, string>([
  ["ok", "bg-[color:var(--hk-ok)]"],
  ["brand", BANK_BG_BRAND],
  ["ink", BANK_BG_INK],
]);

export function ProgressBar({
  label,
  value,
  parts,
  tone = "ok",
  className,
}: ProgressBarProps): React.JSX.Element {
  const drawn: readonly ProgressPart[] = parts ?? [{ value: value ?? 0, tone }];
  const total = clamp(drawn.reduce((sum, part) => sum + clamp(part.value), 0));
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={total}
      data-kit="progress"
      className={classes("flex h-1.5 w-full overflow-hidden", BANK_RADIUS_PILL, BANK_BG_NEUTRAL, className)}
    >
      {drawn.map((part, index) => (
        <i
          // A part has no identity beyond its place in the split.
          key={index}
          data-kit="progress-fill"
          className={classes("block h-full", FILLS.get(part.tone ?? tone))}
          style={{ width: `${String(clamp(part.value))}%` }}
        />
      ))}
    </div>
  );
}
